import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StreetValidationRequest, AddressValidationState, InternationalStreetValidationResponse, USStreetValidationResponse } from "../interfaces/address-validation.interface";
import { mapAddressInternationalValidationMagento, mapAddressUSValidationMagento } from "../mapping/address-validation.mapping";
import { AddressValidationInfo } from "../../checkout/shop-checkout.interface";
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from "../../alert/global-message";
import { GENERIC_TRANSACTION_ERROR, defaultApiState } from "../../../common/constants/api-constants";
import { MessageType } from "../../alert/global-message.interface";
import { StoreState } from "../../../store/store.interface";
import { ActionStatus } from "../../../common/constants/action-status";
import environment from "../../../common/environment";
import { MAGENTO_RECAPTCHA_HEADER, ShopApi } from "../../../common/constants/shop-api";
import { RecaptchaAction, getRecaptchaToken } from "../../../common/recaptcha";
import { getResponseData } from "../../../common/utils/api-utils";

/*----- DECLARE THUNKS ------*/

export const validateAddress = createAsyncThunk('address-validation/validateAddress', 
  async (requestBody: StreetValidationRequest, thunkAPI) => {
    try{
      let address: AddressValidationInfo = {
        address1: requestBody.street1,
        address2: requestBody.street2,
        city: requestBody.city,
        state: requestBody.state,
        zipCode: requestBody.zipCode,
        country: requestBody.country || "US"
      }
      if(requestBody.country === "US"){
        const addressValidationInfo: USStreetValidationResponse = await validateUSStreetAddressApiCall(requestBody);
        if(addressValidationInfo && Object.keys(addressValidationInfo as object).length != 0){
          address = {
            address1: addressValidationInfo.street1,
              address2: addressValidationInfo.street2,
              city: addressValidationInfo.city,
              state: addressValidationInfo.state,
              zipCode: addressValidationInfo.zipCode,
              country: addressValidationInfo.country || "US",
              dpvMatchCode: addressValidationInfo.dpvMatchCode,
              rdiType: addressValidationInfo.rdiType,
              county: addressValidationInfo.county,
          }
        }
      }
      else {
        // For INTL we want to remove the street2 due issue with the response from Address Validation API
        // There's a ticket to update the RMS API (WECOMM-16801)
        const street2RequestBody = requestBody.street2;
        delete requestBody.street2;
        const addressValidationInfo: InternationalStreetValidationResponse | undefined = await validateInternationalStreetAddressApiCall(requestBody);
        if(addressValidationInfo && Object.keys(addressValidationInfo as object).length != 0){
          address = {
            address1: addressValidationInfo.street1,
              address2: street2RequestBody ?? "",
              city: addressValidationInfo.city,
              state: addressValidationInfo.state,
              zipCode: addressValidationInfo.zipCode,
              country: addressValidationInfo.country,
              verificationStatus: addressValidationInfo.verificationStatus
          }
        }
      }
      return address;
    } catch(error) {
      thunkAPI.dispatch(updateGlobalMessage({
        id: PAYMENT_MESSAGE_ID,
        message: GENERIC_TRANSACTION_ERROR,
        type: MessageType.primary
      }));
      return thunkAPI.rejectWithValue(error);
    }
  }
);

/*----- CREATE INITIAL STATE ------*/

export const addressValidationStateInitialState: AddressValidationState = defaultApiState;

/*----- CREATE STORE SLICE ------*/

export const addressValidationSlice = createSlice({
  name: "address-validation",
  initialState: addressValidationStateInitialState,
  reducers:{},
  extraReducers(builder){
    builder
    .addCase(validateAddress.pending, (state) =>{
      state.status = ActionStatus.loading;
    })
    .addCase(validateAddress.fulfilled, (state, action) =>{
      state.status = ActionStatus.succeeded;
      state.data = action.payload;
    })
    .addCase(validateAddress.rejected, (state, action) => {
      state.status = ActionStatus.failed;
      state.error = action.payload;
    })
  }
});

/*----- CREATE SELECTORS ------*/

export const getAddressValidationResponse = (state: StoreState) =>
  state.addressValidationState.data;

export const getAddressValidationStatus = (state: StoreState) =>
  state.addressValidationState.status;

export const getAddressValidationError = (state: StoreState) =>
  state.addressValidationState.error;
  
/*----- CREATE REDUCER ------*/

export const addressValidationReducer = addressValidationSlice.reducer;

/*----- API CALLS ------*/

async function validateUSStreetAddressApiCall(requestBody: StreetValidationRequest): Promise<USStreetValidationResponse>{
  return validateUSStreetAddressApiCallMagento(requestBody);
}

async function validateInternationalStreetAddressApiCall(requestBody: StreetValidationRequest): Promise<InternationalStreetValidationResponse | undefined>{
  return validateInternationalStreetAddressApiCallMagento(requestBody);
}

async function validateUSStreetAddressApiCallMagento(requestBody: StreetValidationRequest): Promise<USStreetValidationResponse>{
  const url = new URL(`${environment().magentoUrl}${ShopApi.addressUSStreetValidationMagento}`);
  const captchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const response = await getResponseData(
    new Request(url.toString(),{
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        [MAGENTO_RECAPTCHA_HEADER]: captchaToken!
      },
      body: JSON.stringify(requestBody)
    })
  );
  return mapAddressUSValidationMagento(response);
}

async function validateInternationalStreetAddressApiCallMagento(requestBody: StreetValidationRequest): Promise<InternationalStreetValidationResponse | undefined>{
  const url = new URL(`${environment().magentoUrl}${ShopApi.addressInternationalStreetValidationMagento}`);
  const captchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const response = await getResponseData(
    new Request(url.toString(),{
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        [MAGENTO_RECAPTCHA_HEADER]: captchaToken!
      },
      body: JSON.stringify(requestBody)
    })
  );

  return mapAddressInternationalValidationMagento(response);
}